import axios from 'axios';

export const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
export const BASE_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

console.log('API URL:', API_URL); // Debug log

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log(`Making ${config.method.toUpperCase()} request to:`, config.url); // Debug log
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
api.interceptors.response.use(
  (response) => {
    console.log('Response from', response.config.url, ':', response.status);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.response?.data);
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (credentials) => api.post('/auth/login', credentials),
  setup: () => api.post('/auth/setup'),
  getMe: () => api.get('/auth/me'),
};

// Courses API
export const coursesAPI = {
  getAll: () => api.get('/courses'),
  getAllAdmin: () => {
    console.log('Fetching all courses for admin...');
    return api.get('/courses/all');
  },
  getById: (id) => api.get(`/courses/${id}`),
  create: (data) => api.post('/courses', data),
  update: (id, data) => api.put(`/courses/${id}`, data),
  delete: (id) => api.delete(`/courses/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/courses/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Leaders API
export const leadersAPI = {
  getAll: () => api.get('/leaders'),
  getAllAdmin: () => api.get('/leaders/all'),
  create: (data) => api.post('/leaders', data),
  update: (id, data) => api.put(`/leaders/${id}`, data),
  delete: (id) => api.delete(`/leaders/${id}`),
  uploadImage: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/leaders/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Placements API
export const placementsAPI = {
  getAll: () => api.get('/placements'),
  getAllAdmin: () => api.get('/placements/all'),
  create: (data) => api.post('/placements', data),
  update: (id, data) => api.put(`/placements/${id}`, data),
  delete: (id) => api.delete(`/placements/${id}`),
};

// Admin API
export const adminAPI = {
  getStats: () => {
    console.log('Fetching stats...');
    return api.get('/admin/stats');
  },
};

// Users API (New)
export const usersAPI = {
  // Get all users
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/users${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  // Get single user by ID
  getById: async (id) => {
    const response = await api.get(`/admin/users/${id}`);
    return response.data;
  },
  
  // Update user
  update: async (id, data) => {
    const response = await api.put(`/admin/users/${id}`, data);
    return response.data;
  },
  
  // Delete user
  delete: async (id) => {
    const response = await api.delete(`/admin/users/${id}`);
    return response.data;
  },
  
  // Get user statistics
  getStats: async () => {
    const response = await api.get('/admin/users/stats');
    return response.data;
  },
};

// Enrollments API (New)
export const enrollmentsAPI = {
  // Get all enrollments
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/admin/enrollments${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  // Get enrollments by course
  getByCourse: async (courseId) => {
    const response = await api.get(`/admin/enrollments/course/${courseId}`);
    return response.data;
  },
  
  // Update enrollment
  update: async (enrollmentId, data) => {
    const response = await api.put(`/admin/enrollments/${enrollmentId}`, data);
    return response.data;
  },
  
  // Get enrollment statistics
  getStats: async () => {
    const response = await api.get('/admin/enrollments/stats/summary');
    return response.data;
  },
  
  // Get course enrollment statistics
  getCourseStats: async () => {
    const enrollments = await enrollmentsAPI.getAll();
    const stats = {};
    enrollments.enrollments?.forEach(enrollment => {
      const courseName = enrollment.course?.title || 'Unknown';
      if (!stats[courseName]) {
        stats[courseName] = {
          total: 0,
          active: 0,
          completed: 0
        };
      }
      stats[courseName].total++;
      if (enrollment.status === 'active') stats[courseName].active++;
      if (enrollment.status === 'completed') stats[courseName].completed++;
    });
    return stats;
  },
  
  // Export enrollments to CSV
  exportToCSV: async (enrollments) => {
    const data = enrollments.enrollments || enrollments;
    
    // Convert to CSV
    const headers = ['Student Name', 'Student Email', 'Course', 'Enrolled Date', 'Status', 'Progress (%)', 'Payment Status'];
    const rows = data.map(enrollment => [
      enrollment.user?.name || 'N/A',
      enrollment.user?.email || 'N/A',
      enrollment.course?.title || 'Unknown Course',
      new Date(enrollment.enrolledAt).toLocaleDateString(),
      enrollment.status,
      enrollment.progress || 0,
      enrollment.paymentStatus || 'pending'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `enrollments_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
};

// Gallery API
export const galleryAPI = {
  getAll: () => api.get('/gallery'),
  getAllAdmin: () => api.get('/gallery/all'),
  create: (data) => api.post('/gallery', data),
  update: (id, data) => api.put(`/gallery/${id}`, data),
  delete: (id) => api.delete(`/gallery/${id}`),
  upload: (file, title, description, category, order = 0, isActive = true) => {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('title', title);
    formData.append('description', description);
    formData.append('category', category);
    formData.append('order', order);
    formData.append('isActive', isActive);
    return api.post('/gallery/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadToExisting: (id, file) => {
    const formData = new FormData();
    formData.append('image', file);
    return api.post(`/gallery/${id}/upload-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Contacts API
export const contactsAPI = {
  // Get all contact inquiries (admin only)
  getAll: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await api.get(`/contact/admin/inquiries${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },
  
  // Get contact by ID
  getById: async (id) => {
    const response = await api.get(`/contact/admin/inquiries/${id}`);
    return response.data;
  },
  
  // Update contact status
  updateStatus: async (id, data) => {
    const response = await api.put(`/contact/admin/inquiries/${id}`, data);
    return response.data;
  },
  
  // Delete contact
  delete: async (id) => {
    const response = await api.delete(`/contact/admin/inquiries/${id}`);
    return response.data;
  },
  
  // Get dashboard stats (admin only)
  getStats: async () => {
    const response = await api.get('/contact/admin/stats');
    return response.data;
  },
  
  // Get contacts by type
  getByType: async (type) => {
    const response = await api.get(`/contact/admin/inquiries?type=${type}`);
    return response.data;
  },
  
  // Get contacts by status
  getByStatus: async (status) => {
    const response = await api.get(`/contact/admin/inquiries?status=${status}`);
    return response.data;
  },
  
  // Update contact with notes and assignment
  updateContact: async (id, updateData) => {
    const response = await api.put(`/contact/admin/inquiries/${id}`, updateData);
    return response.data;
  },
  
  // Bulk delete contacts
  bulkDelete: async (ids) => {
    const promises = ids.map(id => api.delete(`/contact/admin/inquiries/${id}`));
    const responses = await Promise.all(promises);
    return responses.map(r => r.data);
  },
  
  // Export contacts to CSV
  exportToCSV: async (filters = {}) => {
    const contacts = await contactsAPI.getAll(filters);
    const data = contacts.data || contacts;
    
    // Convert to CSV
    const headers = ['Name', 'Email', 'Phone', 'Type', 'Status', 'Course', 'Date', 'Message'];
    const rows = data.map(contact => [
      contact.name,
      contact.email,
      contact.phone,
      contact.inquiryType,
      contact.status,
      contact.course || 'N/A',
      new Date(contact.createdAt).toLocaleDateString(),
      contact.message || 'N/A'
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `contacts_${new Date().toISOString()}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  }
};

// Recruiters API (New - if needed)
export const recruitersAPI = {
  getAll: () => api.get('/recruiters'),
  getAllAdmin: () => api.get('/recruiters/all'),
  create: (data) => api.post('/recruiters', data),
  update: (id, data) => api.put(`/recruiters/${id}`, data),
  delete: (id) => api.delete(`/recruiters/${id}`),
  uploadLogo: (id, file) => {
    const formData = new FormData();
    formData.append('logo', file);
    return api.post(`/recruiters/${id}/upload-logo`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Update adminAPI to include contact, user, and enrollment stats
const originalAdminGetStats = adminAPI.getStats;
adminAPI.getStats = async () => {
  try {
    // Try to get stats from admin endpoint first
    const adminStats = await originalAdminGetStats();
    
    // Also fetch contact stats
    let contactStats = {};
    try {
      const contactStatsData = await contactsAPI.getStats();
      contactStats = contactStatsData.data || contactStatsData;
    } catch (contactError) {
      console.warn('Could not fetch contact stats:', contactError);
    }
    
    // Also fetch user stats
    let userStats = {};
    try {
      const userStatsData = await usersAPI.getAll();
      userStats = {
        totalUsers: userStatsData.stats?.total || 0,
        verifiedUsers: userStatsData.stats?.verified || 0,
        enrolledUsers: userStatsData.stats?.enrolled || 0
      };
    } catch (userError) {
      console.warn('Could not fetch user stats:', userError);
    }
    
    // Also fetch enrollment stats
    let enrollmentStats = {};
    try {
      const enrollmentStatsData = await enrollmentsAPI.getStats();
      enrollmentStats = enrollmentStatsData;
    } catch (enrollmentError) {
      console.warn('Could not fetch enrollment stats:', enrollmentError);
    }
    
    return {
      data: {
        ...adminStats.data,
        ...contactStats,
        ...userStats,
        ...enrollmentStats
      }
    };
  } catch (error) {
    console.error('Error fetching stats:', error);
    throw error;
  }
};

export default api;